'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './TherapySection.module.css';

/* Video slot — drop the therapy reveal video at this exact path. The
   poster (optional) shows the first frame while the video metadata
   downloads; the asset itself plays once on first hover/in-view and
   stops at its last frame. */
const THERAPY_VIDEO = getCompressedAssetUrl(
  '/web_v2/photon/landing-page/therapy.mp4',
);
const THERAPY_POSTER = getCompressedAssetUrl(
  'web_v2/photon/landing-page/therapy-poster.jpg',
);

type Feature = {
  title: string;
  description: string;
};

const getFeatures = (t: (key: string) => string): Feature[] => [
  {
    title: t('photon.therapySection.feature.compactHandheld'),
    description: t('photon.therapySection.feature.compactHandheldDesc'),
  },
  {
    title: t('photon.therapySection.feature.weekLongBattery'),
    description: t('photon.therapySection.feature.weekLongBatteryDesc'),
  },
  {
    title: t('photon.therapySection.feature.oneTouchStart'),
    description: t('photon.therapySection.feature.oneTouchStartDesc'),
  },
];

const TherapySection = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playedRef = useRef(false);
  const t = useTranslations('photon');
  const features = getFeatures(t);

  /* Plays the video exactly once. Used by both onMouseEnter (desktop
     hover) and the IntersectionObserver below (mobile / scroll). */
  const playOnce = useCallback(() => {
    const node = videoRef.current;
    if (!node || playedRef.current) return;
    playedRef.current = true;
    /* Reset to first frame just in case the metadata-load already
       advanced the playhead, then attempt playback. The promise
       returned by .play() can reject (autoplay policy on touch
       devices); silence that — the IO retry covers it. */
    try {
      node.currentTime = 0;
    } catch {
      /* readyState may not be high enough yet — harmless. */
    }
    node.play().catch(() => {
      /* If the browser blocked playback, allow another attempt. */
      playedRef.current = false;
    });
  }, []);

  useEffect(() => {
    const node = videoRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;
    /* Threshold 0.3 = play as soon as ~30% of the video has scrolled
       into view. That fires well before the user reaches the section
       centre, so the playback feels automatic on scroll. */
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            playOnce();
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.3 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [playOnce]);

  return (
    <section className={styles.section} onMouseEnter={playOnce}>
      <div className={styles.contentWrapper}>
        <div className={styles.leftColumn}>
          <div className={styles.videoFrame}>
            <video
              className={styles.therapyVideo}
              ref={videoRef}
              src={THERAPY_VIDEO}
              poster={THERAPY_POSTER}
              muted
              playsInline
              preload="metadata"
              aria-label={t('photon.therapySection.ariaLabel.everydayUse')}
            />
          </div>
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.header}>
            <h2 className={styles.heading}>
              {t('photon.therapySection.heading.whereverYouGo')}
            </h2>
            <p className={styles.description}>
              {t('photon.therapySection.text.builtToTravel')}
            </p>
          </div>
          <div className={styles.featureList}>
            {features.map((feature, i) => (
              <div className={styles.featureItem} key={feature.title}>
                <p className={styles.featureTitle}>{feature.title}</p>
                <p className={styles.featureDescription}>
                  {feature.description}
                </p>
                {i < features.length - 1 && <div className={styles.divider} />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TherapySection;
